import { MutableState } from 'src/replicant/State';
import {
  TournamentType,
  match2TournamentRuleset as ruleset,
} from './match2-tournament.ruleset';
import * as getters from './match2-tournament.getters';

/**
 * Create a tournament event based on given time
 */
export function createTournament(
  state: MutableState,
  eventId: string,
  type: TournamentType,
  now: number,
): void {
  state.match2.tournament.events[eventId] = {
    claimed: false,
    player: {
      score: 0,
      scoreUpdatedAt: now,
    },
    opponentList: [], // will be calculated on next async update call
    multiplier: 0,
    type,
  };

  // Set the lastStartedAt to now
  state.match2.tournament.lastStartedAt = now;

  // To determine what type of tournament the player will be matched in
  state.match2.tournament.currentType = type;
  state.match2.tournament.currentEndAt = getters.getTournamentPeriodById(
    state,
    eventId,
  )?.end;
}

/**
 * Create or update a tournament event based on given time
 */
export function createOrUpdateTournament(
  state: MutableState,
  scoreIncrement: number,
  now: number,
): {
  finalRank?: number | undefined;
  matchMakingCriteria?: string | undefined;
  multiplier: number;
  type: string;
  claimed: boolean;
  player: { score: number; scoreUpdatedAt: number };
  opponentList: string[];
} | null {
  const period = getters.getTournamentPeriod(state, now);

  // If not defined, so there is no tournament for the given time
  if (!period) return null;

  // Initialise the tournament event if not defined
  if (!state.match2.tournament.events[period.eventId]) {
    createTournament(state, period.eventId, period.type, now);
  }

  // get current tournament event
  const event = state.match2.tournament.events[period.eventId];

  if (!event) {
    throw new Error(`Event ${period.eventId} has not been created yet`);
  }

  // Update player score in the tournament event
  if (scoreIncrement > 0) {
    // Reset the event if has been finished thorugh cheats before end date
    if (event.claimed || event.finalRank) {
      event.claimed = false;
      event.finalRank = undefined;
      event.player.score = 0;
      event.player.scoreUpdatedAt = now;
    }

    const { tournamentMultiplier } = ruleset;
    const multiplierIndex = Math.min(
      event.multiplier,
      tournamentMultiplier.length - 1,
    );
    const multiplier = tournamentMultiplier[multiplierIndex];
    event.player.score += scoreIncrement * multiplier;
    event.player.scoreUpdatedAt = now;
  }

  // Add increment to multiplier if event type is streaks.
  if (period.type === 'streak') {
    event.multiplier++;
  }

  // Return the tournament event
  return event;
}

/**
 * Drop the opponent lists of claimed events. `setEventClaimed` does this at claim time; this
 * catches events claimed before that was the case, which are otherwise kept as they were forever.
 */
export function clearClaimedOpponentLists(state: MutableState): void {
  for (const event of Object.values(state.match2.tournament?.events ?? {})) {
    if (event.claimed && event.opponentList.length > 0) {
      event.opponentList = [];
    }
  }
}

/**
 * Set an event as claimed, so it won't be returned by getUnclaimedEvents anymore
 */
export function setEventClaimed(
  state: MutableState,
  id: string,
  now: number,
): void {
  const event = state.match2.tournament?.events?.[id];
  if (!event) {
    throw new Error(`Error in setEventClaimed: event ${id} not found`);
  }
  const period = getters.getTournamentPeriod(state, now);

  // Delete event that has the same id as the current period's event - ie. event type change due to AB test
  if (id === period?.eventId) {
    delete state.match2.tournament.events[id];
  } else {
    event.claimed = true;
    // Nothing reads a claimed event's opponents: leaderboards are fetched for the current and
    // unclaimed events only, and other players look up this player's score through the index doc
    // rather than this list. At ~50 ids per event it is most of what a claimed event holds.
    event.opponentList = [];
  }
}

/**
 * Reset an existing tournament event, used for testing only
 */
export function resetEvent(
  state: MutableState,
  eventId: string,
  now: number,
): void {
  const event = state.match2.tournament?.events?.[eventId];
  if (!event) return;
  event.claimed = false;
  event.finalRank = undefined;
  event.player.score = 0;
  event.player.scoreUpdatedAt = now;
}

/**
 * Reset event multiplier.
 * @param state
 * @param now
 * @returns
 */
export function resetEventMultiplier(state: MutableState, now: number): void {
  const period = getters.getTournamentPeriod(state, now);
  if (!period) return;

  const event = state.match2.tournament?.events?.[period.eventId];
  if (!event) return;

  event.multiplier = 0;
}
